import { useRef, useState } from 'react';

import { Button } from '@/shadcn/button';

import { RichEditor, type RichEditorRef } from '@/components/ui/richEditor';
import { isRichEditorEmpty } from '@/components/ui/richEditor/utils';
import { useUser } from '@/hooks/useUser';
import { useWorkflowContext } from '@/hooks/useWorkflowContext';
import type { ICommentData } from '../commentFeed.view';

type Props = {
  onSave: (comment: ICommentData, id?: number) => void;
  onCancel: (id: number) => void;
  data?: ICommentData;
  id?: number;
};

const checkIsEmptyComment = (comment: string | undefined): boolean => {
  if (!comment) return true;
  return isRichEditorEmpty(JSON.parse(comment));
};

export const CommentForm = ({ data, id, onSave, onCancel }: Props) => {
  const richEditorRef = useRef<RichEditorRef>();
  const [value, setValue] = useState<string | undefined>(data?.comment);
  const workflowContext = useWorkflowContext();

  const isEditing = id !== undefined && data !== undefined;

  const handleChange = (richValue: string | undefined) => {
    setValue(richValue);
  };

  const { user, loading } = useUser();

  if (loading || !user) {
    return null;
  }

  const handleAddOrEditComment = () => {
    if (isEditing) {
      const updatedComment = {
        ...data,
        comment: value
      };
      onSave(updatedComment, id);
    } else {
      const newComment = {
        comment: value,
        date: new Date().toISOString(),
        group: workflowContext?.author?.name || '',
        groupLabel: workflowContext?.author?.label || '',
        user: {
          accountId: user.rowId,
          name: user.name,
          avatar: user.avatar
        }
      };
      onSave(newComment);
    }
    richEditorRef.current?.clearEditor();
  };

  const handleCancel = (): void => {
    // Reset the editor and calls onCancel to close the edit mode
    richEditorRef.current?.clearEditor();
    // ID is guaranteed to be defined since we only call onCancel when editing.
    // and we can edit only if an id is provided.
    onCancel(id!);
  };

  return (
    <>
      <RichEditor
        autoFocus
        // withSubject
        onChange={handleChange}
        ref={richEditorRef}
        value={value}
        withMentions
      />
      <div className="mt-2.5 flex justify-end gap-4">
        {isEditing && (
          <Button
            aria-label="Cancel comment edition"
            data-cy="cancelComment-button"
            onClick={handleCancel}
            variant="outline"
          >
            Cancel
          </Button>
        )}
        <Button
          aria-label={isEditing ? 'Save comment' : 'Add comment'}
          data-cy="addComment-button"
          disabled={checkIsEmptyComment(value)}
          onClick={handleAddOrEditComment}
        >
          {isEditing ? 'Save' : 'Add comment'}
        </Button>
      </div>
    </>
  );
};
